import { ConfigProvider, DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FullPageLoader from "../../../core/common/loader/FullPageLoader";
import { getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { getMyProfile } from "../../../core/data/redux/employeesSlice";
import {
  createNOCExIndiaRequest,
  deleteFileFromAWS,
  fetchNOCExIndiaRequest,
  fetchStayList,
  fetchTravelList,
  uploadFileToAWS,
} from "../../../core/data/redux/nocExIndiaSlice";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
  formatFileSize,
} from "../../../utils/fileUtils";
import { handleDocumentClick } from "../../../utils/documentUtils";
import { all_routes } from "../../router/all_routes";

const { RangePicker } = DatePicker;

interface ForeignVisitCreateProps { }

interface DocumentFile {
  id: string;
  name: string;
  extension: string;
  size: number;
  file: File | null;
  uploading: boolean;
  awsKey?: string;
  uploadedAt?: string | null;
}

const ForeignVisitCreate = (props: ForeignVisitCreateProps) => {
  const routes = all_routes;
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { id } = useParams<{ id: string }>(); // Get ID from URL for edit mode
  const employeesState = useSelector((state: any) => state.employeesState);
  const nocExIndiaState = useSelector((state: any) => state.nocExIndiaState);

  const [showOverview, setShowOverview] = useState(false);
  const user = employeesState?.data?.data;
  const isEditMode = !!id; // Check if we're in edit mode

  // Travel and Stay lists
  const [travelList, setTravelList] = useState<any[]>([]);
  const [stayList, setStayList] = useState<any[]>([]);

  // Form data state matching backend DTO exactly
  const [formData, setFormData] = useState({
    // Employee Details (pre-filled)
    name: "",
    empId: "",
    designation: "",
    dateOfJoining: "",
    employmentStatus: "Direct recruit",
    pay_scale: "",
    department: "",
    contactNumber: "",
    email: "",
    dateOfBirth: "",

    // Application Details
    placeOfVisitNoc: "",
    purposeOfVisit: "",
    periodOfVisitFrom: "",
    periodOfVisitTo: "",
    leaveDetails: "",
    totalMembersTravel: "",

    // Expenditure
    stayingExpenditure: "",
    travelExpenditureA: "",
    otherExpenditureAm: "",
    totalExpenditure: "",
    currency: "INR",

    // Borne By
    tExpenditureBorneBy: "",
    sExpenditureBorneBy: "",
    tRelativesName: "",
    tRelationWithPerson: "",
    sRelativesName: "",
    sRelationWithPerson: "",

    countriesToVisit: "",
    durationOfVisit: "",
    uploadedFilesNote: "",

    // Optional
    dnARPending: "",
    vigilancePending: "",
    dnARStatus: "N",
    dnARPenaltyEffecti: "",
    remarks: "",

    // Certifications
    expenditureCertification: false,
    chknil: "", // 'X' if checkbox is checked, '' if unchecked

    // Declaration
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentFile[]>(
    []
  );
  const [travelEntries, setTravelEntries] = useState([
    {
      Seq: "1",
      Country: "",
      DurationOfVisit: "",
      PurposeOfVisit: "",
    },
  ]);

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Processing...");
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState<"success" | "error">(
    "success"
  );

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Fetch lists on mount
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const travelResponse = await dispatch(fetchTravelList()).unwrap();
        const stayResponse = await dispatch(fetchStayList()).unwrap();
        setTravelList(travelResponse || []);
        setStayList(stayResponse || []);

        // Set default values to first option from API response ONLY in create mode
        if (!isEditMode) {
          if (
            travelResponse &&
            travelResponse.length > 0 &&
            !formData.tExpenditureBorneBy
          ) {
            setFormData((prev) => ({
              ...prev,
              tExpenditureBorneBy:
                travelResponse[0].travel_id || travelResponse[0].id || "",
            }));
          }

          if (
            stayResponse &&
            stayResponse.length > 0 &&
            !formData.sExpenditureBorneBy
          ) {
            setFormData((prev) => ({
              ...prev,
              sExpenditureBorneBy:
                stayResponse[0].stay_id || stayResponse[0].id || "",
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching lists:", error);
        toast.error("Failed to load dropdown options");
      }
    };
    fetchLists();
  }, [dispatch, isEditMode]);

  // Load profile - matching Higher Studies pattern
  useEffect(() => {
    dispatch(getMyProfile({}));
  }, [dispatch]);

  useEffect(() => {
    if (user?.details) {
      const userDetails = user.details;
      setFormData((prev) => ({
        ...prev,
        name: `${userDetails.firstname || ""} ${userDetails.lsastname || userDetails.lastname || ""
          }`.trim(),
        empId: userDetails.empId || "",
        designation:
          userDetails.sap_department_designation_wings?.designation_name ||
          userDetails.designation_details?.designationName ||
          `Designation ID: ${userDetails.designationID || ""}`,
        dateOfJoining: userDetails.date_of_joining
          ? moment(userDetails.date_of_joining).format("DD-MM-YYYY")
          : "",
        employmentStatus:
          userDetails.employmentType || user.employmentType || user.status,
        department:
          userDetails.sap_department_designation_wings?.department_name ||
          userDetails.department_details?.departmentName ||
          `Department ID: ${userDetails.department || ""}`,
        pay_scale: userDetails.pay_scale || "",
        contactNumber: userDetails.phone || "",
        email: userDetails.email || "",
        dateOfBirth: userDetails.birthdate
          ? moment(userDetails.birthdate).format("DD-MM-YYYY")
          : "",
      }));
      setIsLoadingProfile(false);
    } else if (employeesState?.loading === "fulfilled" && !user?.details) {
      setIsLoadingProfile(false);
    }
  }, [user, employeesState?.loading]);

  // Load existing data in edit mode
  useEffect(() => {
    const loadExistingData = async () => {
      if (!isEditMode || !id) return;

      try {
        setIsLoadingProfile(true);
        const response = await dispatch(
          fetchNOCExIndiaRequest({ id: parseInt(id) })
        ).unwrap();
        const data = response?.data || response;

        // Helper function to convert YYYY-MM-DD to DD-MM-YYYY
        const convertDateFromBackend = (dateStr: string) => {
          if (!dateStr) return "";
          const parts = dateStr.split("-");
          if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`; // DD-MM-YYYY
          }
          return dateStr;
        };

        if (data) {
          console.log("Edit Mode - Loaded Data:", data);
          console.log("Travel Borne By:", data.tExpenditureBorneBy);
          console.log("Stay Borne By:", data.sExpenditureBorneBy);
          console.log("Travel List:", travelList);
          console.log("Stay List:", stayList);

          // Populate form data
          setFormData((prev) => ({
            ...prev,
            placeOfVisitNoc: data.placeOfVisitNoc || "",
            purposeOfVisit: data.purposeOfVisit || "",
            periodOfVisitFrom:
              convertDateFromBackend(data.periodOfVisitFrom) || "",
            periodOfVisitTo: convertDateFromBackend(data.periodOfVisitTo) || "",
            leaveDetails: data.leaveDetails || "",
            totalMembersTravel: data.totalMembersTravel || "",
            stayingExpenditure: data.stayingExpenditure || "",
            travelExpenditureA: data.travelExpenditureA || "",
            otherExpenditureAm: data.otherExpenditureAm || "",
            totalExpenditure: data.totalExpenditure || "",
            currency: data.currency || "INR",
            tExpenditureBorneBy: data.tExpenditureBorneBy || "",
            sExpenditureBorneBy: data.sExpenditureBorneBy || "",
            tRelativesName: data.tRelativesName || "",
            tRelationWithPerson: data.tRelationWithPerson || "",
            sRelativesName: data.sRelativesName || "",
            sRelationWithPerson: data.sRelationWithPerson || "",
            dnARPending: data.dnARPending || "",
            vigilancePending: data.vigilancePending || "",
            dnARStatus: data.dnARStatus || "",
            dnARPenaltyEffecti: data.dnARPenaltyEffecti || "",
            remarks: data.remarks || "",
            chknil: data.chknil || "",
            countriesToVisit: data.countriesToVisit || "",
            durationOfVisit: data.durationOfVisit || "",
            uploadedFilesNote: data.uploadedFilesNote || "",
            department: data.department || prev.department || "",
          }));

          // Populate travel entries
          if (
            data.travelEntries &&
            Array.isArray(data.travelEntries) &&
            data.travelEntries.length > 0
          ) {
            setTravelEntries(
              data.travelEntries.map((entry: any, index: number) => ({
                Seq: entry.Seq || (index + 1).toString(),
                Country: entry.Country || "",
                DurationOfVisit: entry.DurationOfVisit || "",
                PurposeOfVisit: entry.PurposeOfVisit || "",
              }))
            );
          }

          // Populate documents
          if (
            data.documents &&
            Array.isArray(data.documents) &&
            data.documents.length > 0
          ) {
            const existingDocs: DocumentFile[] = data.documents.map(
              (doc: any, index: number) => ({
                id: `existing-${index}`,
                name: doc.fileName || `Document ${index + 1}`,
                extension: doc.fileName?.split(".").pop() || "",
                size: 0,
                file: null,
                uploading: false,
                awsKey: doc.fileName,
                uploadedAt: doc.uploadedAt || new Date().toISOString(),
              })
            );
            setUploadedDocuments(existingDocs);
          }

          toast.success("Data loaded successfully");
        }
      } catch (error: any) {
        console.error("Failed to load existing data:", error);
        toast.error("Failed to load existing data");
        navigate(routes.noc.foreignVisit);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadExistingData();
  }, [isEditMode, id, dispatch, navigate, routes.noc.foreignVisit]);

  // Calculate total expenditure
  useEffect(() => {
    const staying = parseFloat(formData.stayingExpenditure) || 0;
    const travel = parseFloat(formData.travelExpenditureA) || 0;
    const other = parseFloat(formData.otherExpenditureAm) || 0;
    const total = staying + travel + other;
    setFormData((prev) => ({
      ...prev,
      totalExpenditure: total > 0 ? total.toString() : "",
    }));
  }, [
    formData.stayingExpenditure,
    formData.travelExpenditureA,
    formData.otherExpenditureAm,
  ]);

  // Validation helpers
  const validateStayingExpenditure = (value: string): boolean => {
    if (!value) return true; // Allow empty
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return false;
    const strValue = Math.floor(Math.abs(numValue)).toString();
    return strValue.length <= 7;
  };

  const validateTravelExpenditure = (value: string): boolean => {
    if (!value) return true; // Allow empty
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return false;
    const strValue = Math.floor(Math.abs(numValue)).toString();
    return strValue.length <= 13;
  };

  const validateOtherExpense = (value: string): boolean => {
    if (!value) return true; // Allow empty
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return false;
    const strValue = Math.floor(Math.abs(numValue)).toString();
    return strValue.length <= 13;
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (name === "totalMembersTravel") {
      const numValue = value.replace(/\D/g, "").slice(0, 2);
      setFormData((prev) => ({ ...prev, [name]: numValue }));
      return;
    }
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;

      // Special handling for chknil checkbox
      if (name === "chknil") {
        setFormData((prev) => ({ ...prev, [name]: checked ? "X" : "" }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: checked }));
      }
      return;
    }

    // Validate expenditure fields
    if (name === "stayingExpenditure") {
      if (!validateStayingExpenditure(value)) {
        toast.error("Staying Expenditure Amount: Maximum 7 digits allowed");
        return;
      }
    } else if (name === "travelExpenditureA") {
      if (!validateTravelExpenditure(value)) {
        toast.error("Travel Expenditure Amount: Maximum 13 digits allowed");
        return;
      }
    } else if (name === "otherExpenditureAm") {
      if (!validateOtherExpense(value)) {
        toast.error("Other Expense: Maximum 13 digits allowed");
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle date changes
  const handleDateChange = (name: string, date: any, dateString: string) => {
    setFormData((prev) => ({ ...prev, [name]: dateString }));
  };

  // Handle travel entry changes
  const handleTravelEntryChange = (
    index: number,
    field: string,
    value: string
  ) => {
    const updated = [...travelEntries];
    updated[index] = { ...updated[index], [field]: value };
    setTravelEntries(updated);
  };

  // Add travel entry
  const addTravelEntry = () => {
    setTravelEntries([
      ...travelEntries,
      {
        Seq: (travelEntries.length + 1).toString(),
        Country: "",
        DurationOfVisit: "",
        PurposeOfVisit: "",
      },
    ]);
  };

  // Remove travel entry
  const removeTravelEntry = (index: number) => {
    if (travelEntries.length > 1) {
      const updated = travelEntries.filter((_, i) => i !== index);
      const resequenced = updated.map((entry, i) => ({
        ...entry,
        Seq: (i + 1).toString(),
      }));
      setTravelEntries(resequenced);
    }
  };

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      // Validate file type
      if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
        toast.error(
          `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(", ").toUpperCase()}`
        );
        continue;
      }

      // Validate file size (2MB limit)
      if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
        toast.error(
          `${file.name}: File size (${formatFileSize(file.size)}) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`
        );
        continue;
      }

      const fileObj: DocumentFile = {
        id: `${Date.now()}-${Math.random()}`,
        name: file.name,
        extension: file.name.split(".").pop() || "",
        size: file.size,
        file,
        uploading: true,
      };

      setUploadedDocuments((prev) => [...prev, fileObj]);

      try {
        const base64String = await convertFileToBase64(file);
        const result = await dispatch(
          uploadFileToAWS({ filestring: base64String })
        ).unwrap();

        console.log("AWS Upload Result:", result);

        // Extract the key from different possible response structures
        const awsKey =
          result?.data?.key ||
          result?.data?.Key ||
          result?.key ||
          result?.Key ||
          null;

        if (!awsKey) {
          console.error("No AWS key found in response:", result);
          toast.error(`Failed to get upload key for ${file.name}`);
          setUploadedDocuments((prev) =>
            prev.filter((d) => d.id !== fileObj.id)
          );
          continue;
        }

        setUploadedDocuments((prev) =>
          prev.map((doc) =>
            doc.id === fileObj.id
              ? { ...doc, uploading: false, awsKey: awsKey }
              : doc
          )
        );

        console.log("Document uploaded successfully with key:", awsKey);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        setUploadedDocuments((prev) => prev.filter((d) => d.id !== fileObj.id));
      }
    }
  };

  // Remove file
  const handleRemoveFile = async (docId: string) => {
    const doc = uploadedDocuments.find((d) => d.id === docId);
    if (!doc) return;
    if (doc.awsKey) {
      try {
        await dispatch(deleteFileFromAWS({ key: doc.awsKey })).unwrap();
      } catch (error) {
        console.error("Failed to delete from AWS:", error);
      }
    }
    setUploadedDocuments((prev) => prev.filter((d) => d.id !== docId));
  };

  // View file - open with universal document handler
  const handleViewFile = (docId: string) => {
    const doc = uploadedDocuments.find((d) => d.id === docId);
    if (!doc) {
      toast.error("Document not found");
      return;
    }

    if (doc.file) {
      handleDocumentClick(doc.file, "NOC_EXINDIA");
    } else if (doc.awsKey) {
      handleDocumentClick({ serverKey: doc.awsKey, fileName: doc.name }, "NOC_EXINDIA");
    } else {
      toast.error("File key not found");
    }
  };

  // Validation
  const validateForm = (isDraft: boolean = false) => {
    if (isDraft) return true;
    if (!formData.placeOfVisitNoc) {
      toast.error("Please enter Place of Visit");
      return false;
    }
    if (!formData.purposeOfVisit) {
      toast.error("Please enter Purpose of Visit");
      return false;
    }
    if (!formData.periodOfVisitFrom || !formData.periodOfVisitTo) {
      toast.error("Please select Period of Visit");
      return false;
    }

    // Validate Period To is greater than Period From
    const fromDate = dayjs(formData.periodOfVisitFrom, "DD-MM-YYYY");
    const toDate = dayjs(formData.periodOfVisitTo, "DD-MM-YYYY");
    if (toDate.isBefore(fromDate) || toDate.isSame(fromDate)) {
      toast.error("Period To must be greater than Period From");
      return false;
    }

    if (!formData.totalMembersTravel) {
      toast.error("Please enter Total Members Travelling");
      return false;
    }
    if (!formData.leaveDetails) {
      toast.error("Please enter Period of Leave");
      return false;
    }

    // Validate expenditure fields
    if (!formData.stayingExpenditure) {
      toast.error("Please enter Staying Expenditure Amount (enter 0 if none)");
      return false;
    } else if (!validateStayingExpenditure(formData.stayingExpenditure)) {
      toast.error("Staying Expenditure Amount: Maximum 7 digits allowed");
      return false;
    }

    if (!formData.travelExpenditureA) {
      toast.error("Please enter Travel Expenditure Amount (enter 0 if none)");
      return false;
    } else if (!validateTravelExpenditure(formData.travelExpenditureA)) {
      toast.error("Travel Expenditure Amount: Maximum 13 digits allowed");
      return false;
    }

    if (!formData.otherExpenditureAm) {
      toast.error("Please enter Other Expense (enter 0 if none)");
      return false;
    } else if (!validateOtherExpense(formData.otherExpenditureAm)) {
      toast.error("Other Expense: Maximum 13 digits allowed");
      return false;
    }

    if (!formData.tExpenditureBorneBy) {
      toast.error("Please select who will bear travel expenditure");
      return false;
    }
    if (!formData.sExpenditureBorneBy) {
      toast.error("Please select who will bear stay expenditure");
      return false;
    }
    if (
      formData.tExpenditureBorneBy === "OTHERS" &&
      (!formData.tRelativesName || !formData.tRelationWithPerson)
    ) {
      toast.error("Please provide name and relation for travel expenditure");
      return false;
    }
    if (
      formData.sExpenditureBorneBy === "OTHERS" &&
      (!formData.sRelativesName || !formData.sRelationWithPerson)
    ) {
      toast.error("Please provide name and relation for stay expenditure");
      return false;
    }

    // Validate D&AR and Vigilance
    if (!formData.dnARPending) {
      toast.error("Please select whether any D&AR is pending");
      return false;
    }
    if (!formData.vigilancePending) {
      toast.error("Please select whether any vigilance is pending");
      return false;
    }

    // Validate travel entries only if checkbox is NOT checked
    if (formData.chknil !== "X") {
      const validTravelEntries = travelEntries.filter(
        (entry) =>
          entry.Country.trim() !== "" ||
          entry.DurationOfVisit.trim() !== "" ||
          entry.PurposeOfVisit.trim() !== ""
      );

      if (validTravelEntries.length === 0) {
        toast.error("Please check the box if you haven't availed Ex-India leave, or add at least one previous travel entry.");
        return false;
      }

      // Check if all filled travel entries have all required fields
      for (let i = 0; i < validTravelEntries.length; i++) {
        const entry = validTravelEntries[i];
        if (!entry.Country.trim()) {
          toast.error(`Please enter country name for entry ${i + 1}`);
          return false;
        }
        if (!entry.DurationOfVisit.trim()) {
          toast.error(`Please enter duration of visit for entry ${i + 1}`);
          return false;
        }
        if (!entry.PurposeOfVisit.trim()) {
          toast.error(`Please enter purpose of visit for entry ${i + 1}`);
          return false;
        }
      }
    }

    if (uploadedDocuments.length === 0) {
      toast.error("Please upload at least one document");
      return false;
    }
    if (!formData.declaration) {
      toast.error("Please accept the declaration");
      return false;
    }
    return true;
  };

  // Submit handler
  const handleSubmit = async (isDraft: boolean = false) => {
    // Validate required fields for draft
    if (isDraft) {
      if (!formData.placeOfVisitNoc || !formData.placeOfVisitNoc.trim()) {
        toast.error("Place of visit is required to save as draft");
        return;
      }

      if (!formData.periodOfVisitFrom || !formData.periodOfVisitTo) {
        toast.error("Period of visit is required to save as draft");
        return;
      }
    }

    if (!validateForm(isDraft)) return;
    setIsSubmitting(true);
    setShowLoadingModal(true);
    setLoadingMessage(
      isEditMode
        ? isDraft
          ? "Updating draft..."
          : "Updating application..."
        : isDraft
          ? "Saving draft..."
          : "Submitting application..."
    );
    try {
      // Helper function to convert DD-MM-YYYY to YYYY-MM-DD
      const convertDateFormat = (dateStr: string) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`; // YYYY-MM-DD
        }
        return dateStr;
      };

      // Base payload with required fields
      const payload: any = {
        is_draft: isDraft,
        // validation_bypass: true,
      };

      // Add ID only if in edit mode
      if (isEditMode && id) {
        payload.id = parseInt(id);
      }

      // Include employee details
      payload.department = formData.department || user?.details?.sap_department_designation_wings?.department_name || user?.details?.department_details?.departmentName || "";

      // Only include fields that have values
      if (formData.placeOfVisitNoc) {
        payload.placeOfVisitNoc = formData.placeOfVisitNoc;
      }

      if (formData.purposeOfVisit) {
        payload.purposeOfVisit = formData.purposeOfVisit;
      }

      if (formData.periodOfVisitFrom) {
        payload.periodOfVisitFrom = convertDateFormat(
          formData.periodOfVisitFrom
        );
      }

      if (formData.periodOfVisitTo) {
        payload.periodOfVisitTo = convertDateFormat(formData.periodOfVisitTo);
      }

      if (formData.leaveDetails) {
        payload.leaveDetails = formData.leaveDetails;
      }

      if (formData.totalMembersTravel) {
        payload.totalMembersTravel = formData.totalMembersTravel;
      }

      if (formData.stayingExpenditure) {
        payload.stayingExpenditure = formData.stayingExpenditure;
      }

      if (formData.travelExpenditureA) {
        payload.travelExpenditureA = formData.travelExpenditureA;
      }

      if (formData.otherExpenditureAm) {
        payload.otherExpenditureAm = formData.otherExpenditureAm;
      }

      if (formData.totalExpenditure) {
        payload.totalExpenditure = formData.totalExpenditure;
      }

      if (formData.tExpenditureBorneBy) {
        payload.tExpenditureBorneBy = formData.tExpenditureBorneBy;
      }

      if (formData.sExpenditureBorneBy) {
        payload.sExpenditureBorneBy = formData.sExpenditureBorneBy;
      }

      if (formData.tRelativesName) {
        payload.tRelativesName = formData.tRelativesName;
      }

      if (formData.tRelationWithPerson) {
        payload.tRelationWithPerson = formData.tRelationWithPerson;
      }

      if (formData.sRelativesName) {
        payload.sRelativesName = formData.sRelativesName;
      }

      if (formData.sRelationWithPerson) {
        payload.sRelationWithPerson = formData.sRelationWithPerson;
      }

      if (formData.dnARPending) {
        payload.dnARPending = formData.dnARPending;
      }

      if (formData.vigilancePending) {
        payload.vigilancePending = formData.vigilancePending;
      }

      if (formData.dnARStatus) {
        payload.dnARStatus = formData.dnARStatus;
      }

      if (formData.countriesToVisit) {
        payload.countriesToVisit = formData.countriesToVisit;
      }

      if (formData.durationOfVisit) {
        payload.durationOfVisit = formData.durationOfVisit;
      }

      if (formData.uploadedFilesNote) {
        payload.uploadedFilesNote = formData.uploadedFilesNote;
      }

      // Always include chknil field
      payload.chknil = formData.chknil;

      // Debug: Log uploaded documents state
      console.log("Uploaded Documents State:", uploadedDocuments);

      // Always include documents array (with proper structure matching backend DTO)
      const documentsArray = uploadedDocuments
        .filter((doc) => doc.awsKey)
        .map((doc) => {
          // Workaround for backend issue where docx MIME type exceeds 40 chars
          const isDocx = doc.extension.toLowerCase() === 'docx';
          return {
            fileName: doc.awsKey!,
            fileExtension: isDocx ? 'doc' : doc.extension,
            fileSize: doc.size.toString(),
            typeOfAttachedDoc: "Upload Documents",
            serverKey: doc.awsKey!,
          };
        });

      console.log("Documents Array after mapping:", documentsArray);

      // Always send documents array, even if empty
      payload.documents = documentsArray;

      // Handle travel entries based on chknil checkbox
      if (formData.chknil === "X") {
        // If checkbox is checked (user has NOT availed leave), send blank array
        payload.travelEntries = [];
      } else {
        // If checkbox is NOT checked, include valid travel entries
        const validTravelEntries = travelEntries.filter(
          (entry) =>
            entry.Country.trim() !== "" &&
            entry.DurationOfVisit.trim() !== "" &&
            entry.PurposeOfVisit.trim() !== ""
        );

        // Always send travelEntries array, even if empty
        payload.travelEntries =
          validTravelEntries.length > 0 ? validTravelEntries : [];
      }

      console.log("Payload being sent:", {
        chknil: payload.chknil,
        documents: payload.documents,
        documentsLength: payload.documents?.length,
        travelEntries: payload.travelEntries,
        travelEntriesLength: payload.travelEntries?.length,
      });

      const response = await dispatch(
        createNOCExIndiaRequest(payload)
      ).unwrap();

      const rawMsg =
        response?.data?.data?.message ||
        response?.data?.message ||
        response?.message;

      const successMessage = isDraft
        ? isEditMode
          ? "Draft updated successfully!"
          : "Draft submitted successfully!"
        : rawMsg &&
            rawMsg.toLowerCase() !== "noc saved" &&
            rawMsg.toLowerCase() !== "created successfully"
          ? rawMsg
          : isEditMode
            ? "Application updated successfully!"
            : "Application submitted successfully!";

      setResponseMessage(successMessage);
      setResponseType("success");

      setTimeout(() => {
        setShowLoadingModal(false);
        setShowResponseModal(true);
      }, 500);
    } catch (error: any) {
      console.error("Submission failed:", error);

      // Extract error message from different possible response structures
      const errorMessage =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.data?.message ||
        error?.data?.message ||
        error?.message ||
        (typeof error === "string" ? error : null) ||
        `Failed to ${isEditMode ? "update" : "submit"} application`;

      setResponseMessage(errorMessage);
      setResponseType("error");
      setShowLoadingModal(false);
      setShowResponseModal(true);
      setIsSubmitting(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.noc.foreignVisit}
                className="text-primary text-decoration-none me-3"
              >
                <i className="ti ti-arrow-left fs-24"></i>
              </Link>
              <h3 className="page-title mb-0">
                {isEditMode
                  ? "Edit NOC Application for Foreign Visit"
                  : "Application for NOC for Foreign Visit"}
              </h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.main}>NOC Services</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.noc.foreignVisit}>Foreign Visit</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? "Edit Request" : "Create Request"}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(false);
          }}
        >
          {/* Employee Details */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Employee Details</h5>
            </div>
            <div className="card-body">
              <div className="row">
                {[
                  { label: "Name", name: "name", value: formData.name },
                  {
                    label: "Employee ID",
                    name: "empId",
                    value: formData.empId,
                  },
                  {
                    label: "Designation",
                    name: "designation",
                    value: formData.designation,
                  },
                  {
                    label: "Date of Joining in DMRC",
                    name: "dateOfJoining",
                    value: formData.dateOfJoining,
                  },
                  {
                    label: "Employment Status",
                    name: "employmentStatus",
                    value: formData.employmentStatus,
                  },
                  {
                    label: "Payscale",
                    name: "pay_scale",
                    value: formData.pay_scale,
                  },
                  {
                    label: "Department",
                    name: "department",
                    value: formData.department,
                    required: true,
                    editable: true,
                  },
                  // {
                  //   label: "Email ID",
                  //   name: "email",
                  //   value: formData.email,
                  //   required: true,
                  //   type: "email",
                  // },
                  // {
                  //   label: "Date of Birth",
                  //   name: "dateOfBirth",
                  //   value: formData.dateOfBirth,
                  // },
                ].map((field) => (
                  <div key={field.name} className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label text-dark fw-medium">
                        {field.label}{" "}
                        {field.required && (
                          <span className="text-danger">*</span>
                        )}
                      </label>
                      <input
                        type={(field as any).type || "text"}
                        className="form-control"
                        name={field.name}
                        value={field.value}
                        onChange={handleInputChange}
                        required={field.required}
                        style={!(field as any).editable ? { backgroundColor: "#f8f9fa" } : {}}
                        readOnly={!(field as any).editable}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Visit Details */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">Visit Details</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="placeOfVisitNoc" className="form-label">
                    Place of Visit <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="placeOfVisitNoc"
                    className="form-control"
                    name="placeOfVisitNoc"
                    value={formData.placeOfVisitNoc}
                    onChange={handleInputChange}
                    placeholder="Enter place of posting"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="purposeOfVisit" className="form-label">
                    Purpose of Visit <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="purposeOfVisit"
                    className="form-control"
                    name="purposeOfVisit"
                    value={formData.purposeOfVisit}
                    onChange={handleInputChange}
                    placeholder="Enter purpose"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label htmlFor="periodOfVisitFrom" className="form-label">
                    Period of Visit From <span className="text-danger">*</span>
                  </label>
                  <ConfigProvider>
                    <DatePicker
                      id="periodOfVisitFrom"
                      className="form-control"
                      format="DD-MM-YYYY"
                      value={
                        formData.periodOfVisitFrom
                          ? dayjs(formData.periodOfVisitFrom, "DD-MM-YYYY")
                          : null
                      }
                      onChange={(date, dateString) =>
                        handleDateChange(
                          "periodOfVisitFrom",
                          date,
                          dateString as string
                        )
                      }
                    />
                  </ConfigProvider>
                </div>
                <div className="col-md-4 mb-3">
                  <label htmlFor="periodOfVisitTo" className="form-label">
                    Period of Visit To <span className="text-danger">*</span>
                  </label>
                  <ConfigProvider>
                    <DatePicker
                      id="periodOfVisitTo"
                      className="form-control"
                      format="DD-MM-YYYY"
                      value={
                        formData.periodOfVisitTo
                          ? dayjs(formData.periodOfVisitTo, "DD-MM-YYYY")
                          : null
                      }
                      onChange={(date, dateString) =>
                        handleDateChange(
                          "periodOfVisitTo",
                          date,
                          dateString as string
                        )
                      }
                      disabledDate={(current) => {
                        // Disable dates before or equal to Period From
                        if (!formData.periodOfVisitFrom) {
                          // If no Period From selected, disable past dates
                          return current && current < dayjs().startOf("day");
                        }
                        const fromDate = dayjs(
                          formData.periodOfVisitFrom,
                          "DD-MM-YYYY"
                        );
                        // Period To must be AFTER Period From (not equal)
                        return current && current <= fromDate.endOf("day");
                      }}
                    />
                  </ConfigProvider>
                </div>
                <div className="col-md-4 mb-3">
                  <label htmlFor="totalMembersTravel" className="form-label">
                    Total Members Travelling{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="totalMembersTravel"
                    className="form-control"
                    name="totalMembersTravel"
                    value={formData.totalMembersTravel}
                    onChange={handleInputChange}
                    placeholder="Max 2 digits"
                    maxLength={2}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="leaveFrom" className="form-label">
                    Period of Leave From <span className="text-danger">*</span>
                  </label>
                  <ConfigProvider>
                    <DatePicker
                      id="leaveFrom"
                      className="form-control"
                      format="DD-MM-YYYY"
                      value={
                        formData.leaveDetails && formData.leaveDetails.includes(" to ")
                          ? dayjs(formData.leaveDetails.split(" to ")[0].trim(), "DD-MM-YYYY")
                          : formData.leaveDetails && dayjs(formData.leaveDetails.trim(), "DD-MM-YYYY").isValid()
                          ? dayjs(formData.leaveDetails.trim(), "DD-MM-YYYY")
                          : null
                      }
                      onChange={(date, dateString) => {
                        const fromStr = dateString as string;
                        setFormData((prev) => {
                          const parts = prev.leaveDetails ? prev.leaveDetails.split(" to ") : [];
                          const toStr = parts.length === 2 ? parts[1].trim() : "";
                          const newLeaveDetails = fromStr && toStr ? `${fromStr} to ${toStr}` : fromStr || toStr || "";
                          return { ...prev, leaveDetails: newLeaveDetails };
                        });
                      }}
                      placeholder="Select From Date"
                    />
                  </ConfigProvider>
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="leaveTo" className="form-label">
                    Period of Leave To <span className="text-danger">*</span>
                  </label>
                  <ConfigProvider>
                    <DatePicker
                      id="leaveTo"
                      className="form-control"
                      format="DD-MM-YYYY"
                      value={
                        formData.leaveDetails && formData.leaveDetails.includes(" to ")
                          ? dayjs(formData.leaveDetails.split(" to ")[1].trim(), "DD-MM-YYYY")
                          : null
                      }
                      onChange={(date, dateString) => {
                        const toStr = dateString as string;
                        setFormData((prev) => {
                          const parts = prev.leaveDetails ? prev.leaveDetails.split(" to ") : [];
                          const fromStr = parts.length > 0 ? parts[0].trim() : "";
                          const newLeaveDetails = fromStr && toStr ? `${fromStr} to ${toStr}` : fromStr || toStr || "";
                          return { ...prev, leaveDetails: newLeaveDetails };
                        });
                      }}
                      disabledDate={(current) => {
                        if (!formData.leaveDetails) return false;
                        const parts = formData.leaveDetails.split(" to ");
                        if (parts[0] && parts[0].trim()) {
                          const fromDate = dayjs(parts[0].trim(), "DD-MM-YYYY");
                          return current && current < fromDate.startOf("day");
                        }
                        return false;
                      }}
                      placeholder="Select To Date"
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>
          </div>

          {/* Expenditure Details */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">Expenditure Details</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label htmlFor="stayingExpenditure" className="form-label">
                    Staying Expenditure Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="stayingExpenditure"
                    className="form-control"
                    name="stayingExpenditure"
                    value={formData.stayingExpenditure}
                    onChange={handleInputChange}
                    placeholder="₹"
                  />
                  <small className="text-muted">Maximum 7 digits</small>
                </div>
                <div className="col-md-3 mb-3">
                  <label htmlFor="travelExpenditureA" className="form-label">
                    Travel Expenditure Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="travelExpenditureA"
                    className="form-control"
                    name="travelExpenditureA"
                    value={formData.travelExpenditureA}
                    onChange={handleInputChange}
                    placeholder="₹"
                  />
                  <small className="text-muted">Maximum 13 digits</small>
                </div>
                <div className="col-md-3 mb-3">
                  <label htmlFor="otherExpenditureAm" className="form-label">
                    Other Expense <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    id="otherExpenditureAm"
                    className="form-control"
                    name="otherExpenditureAm"
                    value={formData.otherExpenditureAm}
                    onChange={handleInputChange}
                    placeholder="₹"
                  />
                  <small className="text-muted">Maximum 13 digits</small>
                </div>
                <div className="col-md-3 mb-3">
                  <label htmlFor="totalExpenditure" className="form-label">
                    Total Expenditure
                  </label>
                  <input
                    type="text"
                    id="totalExpenditure"
                    className="form-control"
                    value={`₹ ${formData.totalExpenditure}`}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Expenditure Borne By */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                The Expenditure for Ex-India visit in respect of travel and stay
                will be borne By
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                {/* Travel Section - Left Column */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="tExpenditureBorneBy" className="form-label">
                      For Travel <span className="text-danger">*</span>
                    </label>
                    <select
                      id="tExpenditureBorneBy"
                      className="form-select"
                      name="tExpenditureBorneBy"
                      value={formData.tExpenditureBorneBy}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {travelList.map((item: any) => (
                        <option key={item.travel_id} value={item.travel_id}>
                          {item.travel_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Show Name and Relation fields when "Others" is selected for Travel */}
                  {travelList.find(
                    (item: any) =>
                      item.travel_id === formData.tExpenditureBorneBy &&
                      item.travel_title?.toLowerCase()?.includes("other")
                  ) && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="tRelativesName" className="form-label">
                            Name (For Travel){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="tRelativesName"
                            className="form-control"
                            name="tRelativesName"
                            value={formData.tRelativesName}
                            onChange={handleInputChange}
                            placeholder="Enter name"
                          />
                        </div>
                        <div className="mb-3">
                          <label
                            htmlFor="tRelationWithPerson"
                            className="form-label"
                          >
                            Relation (For Travel){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="tRelationWithPerson"
                            className="form-control"
                            name="tRelationWithPerson"
                            value={formData.tRelationWithPerson}
                            onChange={handleInputChange}
                            placeholder="Enter relation"
                          />
                        </div>
                      </>
                    )}
                </div>

                {/* Stay Section - Right Column */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="sExpenditureBorneBy" className="form-label">
                      For Stay <span className="text-danger">*</span>
                    </label>
                    <select
                      id="sExpenditureBorneBy"
                      className="form-select"
                      name="sExpenditureBorneBy"
                      value={formData.sExpenditureBorneBy}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      {stayList.map((item: any) => (
                        <option key={item.stay_id} value={item.stay_id}>
                          {item.stay_title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Show Name and Relation fields when "Others" is selected for Stay */}
                  {stayList.find(
                    (item: any) =>
                      item.stay_id === formData.sExpenditureBorneBy &&
                      item.stay_title?.toLowerCase()?.includes("other")
                  ) && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="sRelativesName" className="form-label">
                            Name (For Stay) <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="sRelativesName"
                            className="form-control"
                            name="sRelativesName"
                            value={formData.sRelativesName}
                            onChange={handleInputChange}
                            placeholder="Enter name"
                          />
                        </div>
                        <div className="mb-3">
                          <label
                            htmlFor="sRelationWithPerson"
                            className="form-label"
                          >
                            Relation (For Stay){" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            id="sRelationWithPerson"
                            className="form-control"
                            name="sRelationWithPerson"
                            value={formData.sRelationWithPerson}
                            onChange={handleInputChange}
                            placeholder="Enter relation"
                          />
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>

          {/* Mandatory Attachments */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                Mandatory Attachments <span className="text-danger">*</span>
              </h5>
            </div>
            <div className="card-body">
              {/* Important Notes */}
              <div className="alert alert-info mb-3" role="alert">
                <h6 className="alert-heading mb-2">
                  <i className="ti ti-info-circle me-2"></i>Important Notes:
                </h6>
                <ul className="mb-0" style={{ paddingLeft: "20px" }}>
                  <li className="mb-2">
                    - In case of expense are borne by employee himself/spouse,
                    proof of saving like last six months bank statement is to be
                    provided.
                  </li>
                  <li className="mb-2">
                    - In case of stay travel arrangement made by relative abroad,
                    the written declaration for the same is to be attached.
                  </li>
                  <li className="mb-2">
                    - In case of stay travel arrangement made by relative abroad,
                    proof of domicile is to be attached.
                  </li>
                  <li className="mb-0">
                    - Screenshot of approved/sanctioned leave requests for
                    ex-India must be attached.
                  </li>
                </ul>
              </div>

              <input
                type="file"
                className="form-control mb-3"
                onChange={handleFileUpload}
                multiple
              />
              {uploadedDocuments.length > 0 && (
                <table className="table table-bordered mb-3">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Document Name</th>
                      <th>Size</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedDocuments.map((doc, index) => (
                      <tr key={doc.id}>
                        <td>{index + 1}</td>
                        <td>{doc.name}</td>
                        <td>{(doc.size / 1024).toFixed(2)} KB</td>
                        <td>{doc.uploading ? "Uploading..." : "Uploaded"}</td>
                        <td>
                          <div className="d-flex gap-2">
                            {doc.awsKey && !doc.uploading && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleViewFile(doc.id);
                                }}
                                title="View file"
                              >
                                <i className="ti ti-eye"></i>
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => handleRemoveFile(doc.id)}
                              disabled={doc.uploading}
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Expenditure Certification Checkbox */}
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="expenditureCertification"
                  name="expenditureCertification"
                  checked={formData.expenditureCertification}
                  onChange={handleInputChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="expenditureCertification"
                >
                  It is certified that the expenditure has been /will be debited
                  from this account statement only.
                </label>
              </div>
            </div>
          </div>

          {/* Previously Availed Ex-India Leave */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">
                Previously availed Ex-India leave during the last 5 years <span className="text-danger">*</span>
              </h5>
            </div>
            <div className="card-body">
              {/* Checkbox - I have not availed */}
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="chknil"
                  name="chknil"
                  checked={formData.chknil === "X"}
                  onChange={handleInputChange}

                />
                <label className="form-check-label" htmlFor="chknil">
                  I have not availed Ex-India leave in last 5 years
                </label>
              </div>

              {/* Show table only if checkbox is NOT checked */}
              {formData.chknil !== "X" && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={addTravelEntry}
                    >
                      <i className="ti ti-plus me-1"></i>ADD LINE
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th style={{ width: "8%" }}>Sr. No.</th>
                          <th style={{ width: "26%" }}>Country Visited</th>
                          <th style={{ width: "33%" }}>
                            Duration of Visit (From - To date)
                            (dd/mm/yyyy-dd/mm/yyyy)
                          </th>
                          <th style={{ width: "26%" }}>Purpose of Visit</th>
                          <th style={{ width: "7%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {travelEntries.map((entry, index) => (
                          <tr key={index}>
                            <td className="text-center">{entry.Seq}</td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={entry.Country}
                                onChange={(e) =>
                                  handleTravelEntryChange(
                                    index,
                                    "Country",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter country"
                              />
                            </td>
                            <td>
                              <RangePicker
                                className="form-control form-control-sm d-flex flex-row align-items-center"
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                                placeholder={["Start Date", "End Date"]}
                                size="small"
                                disabledDate={(current) => current && current > dayjs().endOf("day")}
                                value={
                                  entry.DurationOfVisit
                                    ? (() => {
                                      try {
                                        const dates =
                                          entry.DurationOfVisit.split("-");
                                        if (dates.length === 2) {
                                          return [
                                            dayjs(
                                              dates[0].trim(),
                                              "DD/MM/YYYY"
                                            ),
                                            dayjs(
                                              dates[1].trim(),
                                              "DD/MM/YYYY"
                                            ),
                                          ];
                                        }
                                        return null;
                                      } catch {
                                        return null;
                                      }
                                    })()
                                    : null
                                }
                                onChange={(dates, dateStrings) => {
                                  if (dates && dates[0] && dates[1]) {
                                    // Format: "DD/MM/YYYY-DD/MM/YYYY" (hyphen separator with slash dates)
                                    const formattedRange = `${dateStrings[0]}-${dateStrings[1]}`;
                                    handleTravelEntryChange(
                                      index,
                                      "DurationOfVisit",
                                      formattedRange
                                    );
                                  } else {
                                    handleTravelEntryChange(
                                      index,
                                      "DurationOfVisit",
                                      ""
                                    );
                                  }
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={entry.PurposeOfVisit}
                                onChange={(e) =>
                                  handleTravelEntryChange(
                                    index,
                                    "PurposeOfVisit",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter purpose"
                              />
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={() => {
                                    if (!entry.Country.trim() || !entry.DurationOfVisit.trim() || !entry.PurposeOfVisit.trim()) {
                                      toast.warning("Please fill Country, Duration, and Purpose before saving this entry.");
                                    } else {
                                      toast.success(`Entry ${entry.Seq} details saved.`);
                                    }
                                  }}
                                  title="Save this entry"
                                >
                                  <i className="ti ti-device-floppy"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeTravelEntry(index)}
                                  disabled={travelEntries.length === 1}
                                  title="Delete this row"
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* D&AR and Vigilance Status */}
          <div className="card mb-4">
            <div className="card-header bg-light">
              <h5 className="mb-0">D&AR and Vigilance Status</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Whether any D&AR pending?{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <div className="mt-2">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="dnARPending"
                        id="dnARPending_yes"
                        value="Y"
                        checked={formData.dnARPending === "Y"}
                        onChange={handleInputChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="dnARPending_yes"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="dnARPending"
                        id="dnARPending_no"
                        value="N"
                        checked={formData.dnARPending === "N"}
                        onChange={handleInputChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="dnARPending_no"
                      >
                        No
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Whether any vigilance pending?{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <div className="mt-2">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="vigilancePending"
                        id="vigilancePending_yes"
                        value="Y"
                        checked={formData.vigilancePending === "Y"}
                        onChange={handleInputChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="vigilancePending_yes"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="vigilancePending"
                        id="vigilancePending_no"
                        value="N"
                        checked={formData.vigilancePending === "N"}
                        onChange={handleInputChange}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="vigilancePending_no"
                      >
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Declaration */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={handleInputChange}
                />
                <label className="form-check-label">
                  I certify that I am not involved in any court/police/vigilance case and there is no restriction placed by any authority on foreign travel.{" "}
                  <span className="text-danger">*</span>
                </label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Link
              to={routes.noc.foreignVisit}
              className="btn btn-outline-secondary px-4"
            >
              <i className="ti ti-arrow-left me-1"></i>
              Cancel
            </Link>
            <button
              type="button"
              className="btn btn-secondary px-4"
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting}
            >
              <i className="ti ti-device-floppy me-1"></i>
              Save as Draft
            </button>
            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={isSubmitting}
            >
              <i className="ti ti-send me-1"></i>
              Submit
            </button>
          </div>
        </form>

        {/* Overview Modal */}
        {showOverview && (
          <>
            {/* Backdrop */}
            <div
              className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
              style={{ zIndex: 1040 }}
              onClick={() => setShowOverview(false)}
            />

            {/* Overview Modal */}
            <div
              className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
              style={{
                zIndex: 1050,
                width: "90%",
                maxWidth: "700px",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              {/* Modal Header */}
              <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light">
                <h5 className="mb-0 text-dark fw-bold">
                  NOC ex-india leave overview
                </h5>
                <button
                  className="btn btn-link text-dark text-decoration-none p-0"
                  onClick={() => setShowOverview(false)}
                >
                  <i className="ti ti-x fs-24"></i>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4" style={{ lineHeight: "1.6" }}>
                <h6 className="fw-bold mb-3 text-center">
                  Check List for applying for NOC for Ex-India Leave/विदेश जाने
                  के लिए अवकाश हेतु एनओसी की चैकलिस्ट
                </h6>

                <h6 className="fw-bold mb-3">
                  List of documents to be Uploaded / अपलोड किए जाने वाले आवश्यक
                  दस्तावेजों की सूची:
                </h6>

                <div className="text-dark">
                  {/* Section 1 */}
                  <p className="mb-2">
                    <strong>
                      1. If expenditure for Ex-India visit in respect of travel
                      and stay will be borne by Employee
                    </strong>
                  </p>
                  <p className="mb-3">
                    <strong>
                      1. यदि भारत से बाहर जाने पर यात्रा और ठहरने के खर्च में
                      व्यय कर्मचारी द्वारा वहन किया जाएगा
                    </strong>
                  </p>
                  <ol className="ms-4 mb-3">
                    <li>Bank Statement / बैंक स्टेटमेंट</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ol>

                  {/* Section 2 */}
                  <p className="mb-2">
                    <strong>
                      2. If expenditure for Ex-India visit in respect of travel
                      and stay will be borne by Spouse
                    </strong>
                  </p>
                  <p className="mb-3">
                    <strong>
                      2. यदि भारत से बाहर जाने पर यात्रा और ठहरने के खर्च में
                      व्यय पति/पत्नी द्वारा वहन किया जाएगा
                    </strong>
                  </p>
                  <ol className="ms-4 mb-3">
                    <li>
                      Bank Statement of spouse / पति/पत्नी का बैंक स्टेटमेंट
                    </li>
                    <li>Undertaking by spouse / पति/पत्नी द्वारा अंडरटेकिंग</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ol>

                  {/* Section 3 */}
                  <p className="mb-2">
                    <strong>
                      3. If expenditure for Ex-India visit in respect of travel
                      and stay will be borne by relative /friend/Company
                    </strong>
                  </p>
                  <p className="mb-3">
                    <strong>
                      3. यदि विदेश यात्रा के लिए यात्रा और ठहरने का खर्च
                      रिश्तेदार/मित्र/कंपनी द्वारा वहन किया जाएगा
                    </strong>
                  </p>
                  <ol className="ms-4 mb-3">
                    <li>
                      Invitation Letter from relative/friend/Company /
                      रिश्तेदार/मित्र/कंपनी से निमंत्रण
                    </li>
                    <li>Address Proof / निवास प्रमाणपत्र</li>
                    <li>Undertaking / अंडरटेकिंग</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ol>

                  {/* Section 4 */}
                  <p className="mb-2">
                    <strong>
                      4. If expenditure for Ex-India visit in respect of travel
                      will be borne by employee and stay will be borne by
                      relative/friend/Company
                    </strong>
                  </p>
                  <p className="mb-3">
                    <strong>
                      4. यदि यात्रा के खर्च में भारत से बाहर जाने का खर्च
                      कर्मचारी द्वारा वहन किया जाएगा और रहने का खर्च
                      रिश्तेदार/मित्र/कंपनी द्वारा वहन किया जाएगा।
                    </strong>
                  </p>
                  <ol className="ms-4 mb-3">
                    <li>
                      Invitation Letter from relative/friend/Company /
                      रिश्तेदार/मित्र/कंपनी से निमंत्रण
                    </li>
                    <li>Address Proof / निवास प्रमाणपत्र</li>
                    <li>
                      Bank Statement of Employee / कर्मचारी का बैंक स्टेटमेंट
                    </li>
                    <li>Undertaking / अंडरटेकिंग</li>
                    <li>Leave Approval / छुट्टी का अनुमोदन</li>
                    <li>D & AR Status / डीएंडएआर की स्थिति</li>
                  </ol>
                </div>
              </div>

              {/* OK Button */}
              <div className="d-flex justify-content-end p-4 pt-0">
                <button
                  type="button"
                  className="btn text-white px-5 py-2 fw-bold"
                  style={{
                    backgroundColor: "#B85450",
                    borderRadius: "25px",
                    fontSize: "16px",
                  }}
                  onClick={() => setShowOverview(false)}
                >
                  OK
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Full Page Loader */}
      <FullPageLoader show={showLoadingModal} message={loadingMessage} />

      {/* Response Modal */}
      {showResponseModal && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ zIndex: 2000 }}
          />

          {/* Response Modal */}
          <div
            className="position-fixed top-50 start-50 translate-middle bg-white shadow-lg rounded"
            style={{
              zIndex: 2001,
              width: "90%",
              maxWidth: "500px",
            }}
          >
            {/* Modal Header */}
            <div
              className={`d-flex align-items-center justify-content-between p-3 border-bottom ${responseType === "success" ? "bg-success" : "bg-info"
                }`}
            >
              <h5 className="mb-0 text-white fw-bold">
                {responseType === "success" ? (
                  <>
                    <i className="ti ti-check-circle me-2"></i>
                    Success
                  </>
                ) : (
                  <>
                    <i className="ti ti-info-circle me-2"></i>
                    Notice
                  </>
                )}
              </h5>
              <button
                className="btn btn-link text-white text-decoration-none p-0"
                onClick={() => {
                  setShowResponseModal(false);
                  if (responseType === "success") {
                    navigate(routes.noc.foreignVisit);
                  }
                }}
              >
                <i className="ti ti-x fs-20"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4">
              <div
                className={`p-3 rounded ${responseType === "success"
                  ? "bg-success bg-opacity-10"
                  : "bg-info bg-opacity-10"
                  }`}
              >
                <p className="mb-0 text-dark">{responseMessage}</p>
              </div>

              <div className="d-flex justify-content-center mt-4">
                <button
                  type="button"
                  className={`btn ${responseType === "success" ? "btn-success" : "btn-info"
                    } px-5`}
                  onClick={() => {
                    setShowResponseModal(false);
                    if (responseType === "success") {
                      navigate(routes.noc.foreignVisit);
                    }
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ForeignVisitCreate;
